import type { Commands } from "@/api/commands";
import { formatScriptRef } from "@/lib/script-ref";

type RequestFn = Pick<Commands, "validateConfigurable" | "updateScript" | "createScript">;

export type SavedScript = {
  id: string;
  rev_no: number;
  name: string;
  source: string;
};

/**
 * Validate Pine, then create or update a script (new revision when source changes).
 * Throws with the validation/backend error message on failure.
 */
export async function saveValidatedScript(
  request: RequestFn,
  opts: {
    scriptId?: string | null;
    name: string;
    source: string;
  }
): Promise<SavedScript> {
  const source = opts.source.trim();
  if (!source) throw new Error("Script source is required");
  const name = opts.name.trim() || "Untitled script";

  const validation = (await request.validateConfigurable({
    category: "strategy",
    name: "pinescript",
    config: { source },
  })) as { valid?: boolean; error?: string };

  if (validation && validation.valid === false) {
    throw new Error(validation.error || "Script validation failed");
  }

  if (opts.scriptId) {
    const updated = (await request.updateScript({
      id: opts.scriptId,
      name,
      source,
    })) as { id?: string; rev_no?: number; name?: string; source?: string };
    return {
      id: opts.scriptId,
      rev_no: updated.rev_no ?? 1,
      name: updated.name ?? name,
      source: updated.source ?? source,
    };
  }

  const created = (await request.createScript({
    name,
    description: "",
    language: "pine",
    source,
  })) as { id: string; rev_no?: number; name?: string; source?: string };

  if (!created?.id) {
    throw new Error("Script create did not return an id");
  }

  return {
    id: created.id,
    rev_no: created.rev_no ?? 1,
    name: created.name ?? name,
    source: created.source ?? source,
  };
}

/** Pin strategy_config.source to script://id?rev=N for a saved script. */
export function pinStrategySource(
  strategyConfig: Record<string, unknown>,
  saved: SavedScript
): Record<string, unknown> {
  if (!saved?.id) {
    throw new Error("Cannot pin strategy source: script id is missing");
  }
  const rev = saved.rev_no >= 1 ? saved.rev_no : 1;
  return {
    ...strategyConfig,
    source: formatScriptRef(saved.id, rev),
  };
}

/** True when strategy_config.source is a pinned script://id?rev=N URI. */
export function hasPinnedStrategySource(strategyConfig?: Record<string, unknown> | null): boolean {
  const source = String(strategyConfig?.source || "");
  return source.startsWith("script://") && source.includes("?rev=");
}

/** Read metadata.ui.script_id when present. */
export function scriptIdFromMetadata(metadata?: Record<string, unknown> | null): string {
  const ui = metadata?.ui;
  if (!ui || typeof ui !== "object") return "";
  const id = (ui as { script_id?: unknown }).script_id;
  return typeof id === "string" ? id.trim() : "";
}

/**
 * Ensure pinescript strategy_config has a pinned script:// source.
 * Lab create historically dropped source while still saving metadata.ui.script_id;
 * heal from that id + known script revision so Run works without a backend change.
 */
export function ensurePinnedStrategyConfig(
  strategyConfig: Record<string, unknown> | undefined | null,
  opts: {
    metadata?: Record<string, unknown> | null;
    scripts?: { id: string; rev_no?: number }[];
    strategyProvider?: string;
  }
): Record<string, unknown> {
  const cfg = { ...(strategyConfig || {}) };
  if (opts.strategyProvider && opts.strategyProvider !== "pinescript") return cfg;
  if (hasPinnedStrategySource(cfg)) return cfg;

  const scriptId = scriptIdFromMetadata(opts.metadata);
  if (!scriptId) return cfg;

  const matched = opts.scripts?.find((s) => s.id === scriptId);
  const rev = matched?.rev_no && matched.rev_no >= 1 ? matched.rev_no : 1;
  return { ...cfg, source: formatScriptRef(scriptId, rev) };
}
